import { DialogComponent } from '@theme/dialog';
import { isClickedOutside, onAnimationEnd } from '@theme/utilities';

const SEARCH_INPUT_SELECTOR = '#header-search';
const SEARCHANISE_WAIT_MS = 5000;

/**
 * Poll until Searchanise app embed is available.
 * @param {number} maxMs
 * @returns {Promise<object|null>}
 */
function waitForSearchanise(maxMs = SEARCHANISE_WAIT_MS) {
  return new Promise((resolve) => {
    if (window.Searchanise && typeof window.Searchanise.Init === 'function') {
      resolve(window.Searchanise);
      return;
    }

    const start = Date.now();
    const timer = window.setInterval(() => {
      if (window.Searchanise && typeof window.Searchanise.Init === 'function') {
        window.clearInterval(timer);
        resolve(window.Searchanise);
      } else if (Date.now() - start >= maxMs) {
        window.clearInterval(timer);
        resolve(null);
      }
    }, 100);
  });
}

/**
 * Search overlay for Searchanise instant search.
 * Uses dialog.show() (not showModal) so .snize-ac-results stays above the backdrop.
 */
class NcSearchModal extends DialogComponent {
  requiredRefs = ['dialog', 'backdrop'];

  #previousScrollY = 0;

  /**
   * Init Searchanise once per modal open — never on input keystrokes.
   */
  async #initSearchaniseOnOpen() {
    if (this.dataset.snizeOpenInit === 'true') return;

    const sn = await waitForSearchanise();
    if (!sn) return;

    sn.Init();
    this.dataset.snizeOpenInit = 'true';
  }

  showDialog() {
    const { dialog, backdrop } = this.refs;
    if (dialog.open) return;

    this.#previousScrollY = window.scrollY;

    requestAnimationFrame(() => {
      document.body.style.width = '100%';
      document.body.style.position = 'fixed';
      document.body.style.top = `-${this.#previousScrollY}px`;
      document.documentElement.classList.add('nc-search-modal-open');
      this.classList.add('search-modal--open');

      backdrop.hidden = false;
      dialog.show();

      void this.#initSearchaniseOnOpen();

      const input = this.querySelector(SEARCH_INPUT_SELECTOR);
      if (input) {
        window.setTimeout(() => input.focus(), 100);
      }

      this.addEventListener('click', this.#handleClick);
      this.addEventListener('keydown', this.#handleKeyDown);
    });
  }

  closeDialog = async () => {
    const { dialog, backdrop } = this.refs;
    if (!dialog.open) return;

    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('keydown', this.#handleKeyDown);

    dialog.style.animation = 'none';
    void dialog.offsetWidth;
    dialog.classList.add('dialog-closing');
    dialog.style.animation = '';

    await onAnimationEnd(dialog, undefined, { subtree: false });

    document.body.style.width = '';
    document.body.style.position = '';
    document.body.style.top = '';
    window.scrollTo({ top: this.#previousScrollY, behavior: 'instant' });

    dialog.close();
    dialog.classList.remove('dialog-closing');
    backdrop.hidden = true;
    this.classList.remove('search-modal--open');
    document.documentElement.classList.remove('nc-search-modal-open');

    this.dataset.snizeOpenInit = '';

    const input = this.querySelector(SEARCH_INPUT_SELECTOR);
    if (input) {
      input.value = '';
    }
  };

  #handleClick = (event) => {
    const { dialog, backdrop } = this.refs;
    if (event.target === backdrop || isClickedOutside(event, dialog)) {
      this.closeDialog();
    }
  };

  #handleKeyDown = (event) => {
    if (event.key !== 'Escape') return;
    event.preventDefault();
    this.closeDialog();
  };
}

if (!customElements.get('nc-search-modal')) {
  customElements.define('nc-search-modal', NcSearchModal);
}

function bindSearchTriggers() {
  var modal = document.getElementById('search-modal');
  if (!modal || modal.dataset.ncTriggersBound === 'true') return;
  modal.dataset.ncTriggersBound = 'true';

  document.addEventListener(
    'click',
    function (event) {
      var button = event.target.closest('.search-action button');
      if (!button) return;
      if (typeof modal.showDialog !== 'function') return;
      modal.showDialog();
    },
    true
  );
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', bindSearchTriggers);
} else {
  bindSearchTriggers();
}

customElements.whenDefined('nc-search-modal').then(bindSearchTriggers);
